/**
 * A ENCENAÇÃO do plano, COMPARTILHADA (§5.1). O cartão flutuante e a bolha
 * ("Usando skill /panorama") desenham o MESMO plano lado a lado: com uma fila
 * por componente elas divergiam — o cartão na etapa 3 e a bolha já em "7 de 7".
 * Aqui mora o único relógio: quantas etapas a TELA já revelou (`revelados`) do
 * plano corrente (`assinatura`, os rótulos na ordem).
 *
 * Singleton reativo fora de componente, no molde do `utils/motor-de-voz.js` —
 * quem ALIMENTA é o cartão (`progresso-encenado-mixin.js`), quem LÊ é ele, a
 * bolha (`chat-message-plano.vue`) e o fim de turno (`messages-painel-do-chat-
 * mixin.js`, que espera a fila drenar antes de abrir a entrega). Plano de
 * assinatura diferente (histórico reaberto, outra conversa) NÃO é encenado:
 * `reveladosDe` devolve o total e cada consumidor mostra o estado final.
 */
import Vue from 'vue'
import {
  DWELL_DA_ENCENACAO_MS,
  PISO_DA_ENCENACAO_MS,
  TETO_DA_ENCENACAO_MS,
} from '@modules/aawz-intelligence-v2/components/painel-do-chat/painel-do-chat.constantes'
import { intervaloDaEncenacao } from './encenacao-do-progresso'

const CONSULTA_DE_MOVIMENTO_REDUZIDO = '(prefers-reduced-motion: reduce)'

export const encenacaoDoPlano = new Vue({
  data() {
    return { assinatura: '', revelados: 0, alvo: 0, total: 0 }
  },
})

let temporizador = null

export function prefereMovimentoReduzido() {
  try {
    return Boolean(globalThis.matchMedia && globalThis.matchMedia(CONSULTA_DE_MOVIMENTO_REDUZIDO).matches)
  } catch {
    return false
  }
}

function pararTemporizador() {
  if (!temporizador) return
  clearTimeout(temporizador)
  temporizador = null
}

function agendarProximoPasso() {
  if (temporizador) return
  if (encenacaoDoPlano.revelados >= encenacaoDoPlano.alvo) return
  const intervalo = intervaloDaEncenacao({
    total: encenacaoDoPlano.total,
    dwell: DWELL_DA_ENCENACAO_MS,
    teto: TETO_DA_ENCENACAO_MS,
    piso: PISO_DA_ENCENACAO_MS,
  })
  temporizador = setTimeout(function () {
    temporizador = null
    encenacaoDoPlano.revelados += 1
    agendarProximoPasso()
  }, intervalo)
}

/**
 * Alimenta o relógio com o plano corrente. Assinatura nova recomeça do zero;
 * plano que encolheu (ou foi corrigido) reconcilia na hora — a verdade vence.
 */
export function sincronizarEncenacao({ assinatura, total, alvo }) {
  if (prefereMovimentoReduzido()) {
    pararTemporizador()
    encenacaoDoPlano.assinatura = ''
    return
  }
  if (assinatura !== encenacaoDoPlano.assinatura) {
    pararTemporizador()
    encenacaoDoPlano.assinatura = assinatura
    encenacaoDoPlano.revelados = 0
  }
  encenacaoDoPlano.total = total
  encenacaoDoPlano.alvo = alvo
  if (encenacaoDoPlano.revelados > alvo) {
    pararTemporizador()
    encenacaoDoPlano.revelados = alvo
  }
  agendarProximoPasso()
}

/** Quantas etapas a tela já revelou para ESTE plano (outro plano = todas). */
export function reveladosDe(assinatura, total) {
  if (!assinatura || assinatura !== encenacaoDoPlano.assinatura) return total
  return encenacaoDoPlano.revelados
}

/** A fila ainda está andando? (o fim de turno espera por ela). */
export function encenacaoDrenando() {
  return encenacaoDoPlano.revelados < encenacaoDoPlano.alvo
}

/** Trocar de conversa/turno: o relógio não carrega o plano anterior. */
export function reiniciarEncenacao() {
  pararTemporizador()
  encenacaoDoPlano.assinatura = ''
  encenacaoDoPlano.revelados = 0
  encenacaoDoPlano.alvo = 0
  encenacaoDoPlano.total = 0
}
